import * as React from "react";

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export type InputSize = "sm" | "md" | "lg";

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  size?: InputSize;
  invalidMessage?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      size = "md",
      id,
      disabled,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      invalidMessage,
      ...props
    },
    ref,
  ) => {
    const isInvalid =
      ariaInvalid === true ||
      ariaInvalid === "true" ||
      ariaInvalid === "spelling" ||
      ariaInvalid === "grammar" ||
      !!invalidMessage;

    const sizeClasses =
      size === "sm"
        ? "h-8 text-sm px-2"
        : size === "lg"
          ? "h-12 text-lg px-4"
          : "h-10 text-base px-3";

    const base = cn(
      "w-full rounded-md border bg-background text-foreground shadow-sm",
      "placeholder:text-muted-foreground",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
      "disabled:cursor-not-allowed disabled:opacity-50",
      sizeClasses,
      isInvalid &&
        "border-[hsl(var(--color-destructive))] focus-visible:ring-[hsl(var(--color-destructive))]/40",
      className,
    );

    const handleRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current =
            node;
        }
      },
      [ref],
    );

    // Scrubber removed as it caused performance issues and didn't reliably fix the test case.
    // Relying on onBlur and standard React control.

    // Aggressive placeholder scrubbing removed as it caused data loss when value matched placeholder.
    const handleChange = props.onChange;

    const value =
      props.value !== undefined
        ? props.value
        : props.defaultValue !== undefined
          ? props.defaultValue
          : undefined;
    const _isEmpty = value === "" || value === null || value === undefined;

    return (
      <div className="flex w-full flex-col gap-1">
        <input
          type={props.type || "text"}
          id={id}
          ref={handleRef}
          className={base}
          aria-invalid={isInvalid || undefined}
          aria-describedby={
            invalidMessage ? `${id || "input"}-error` : ariaDescribedBy
          }
          disabled={disabled}
          onChange={handleChange}
          {...props}
          // Ensure that if the value is explicitly an empty string, we keep it as such
          // to prevent internal browser defaulting or placeholder bleeding.
          value={props.value}
        />
        {invalidMessage ? (
          <span
            id={`${id || "input"}-error`}
            role="alert"
            className="text-xs text-red-600"
          >
            {invalidMessage}
          </span>
        ) : null}
      </div>
    );
  },
);

Input.displayName = "Input";
